import { Callout, LoaderScreen } from '@/components/Elements';
import { Pagination } from '@/features/analyze/components/Pagination';
import {
  FragmentOf,
  graphql,
  readFragment,
  type ApolloError,
} from '@/lib/graphql';
import { SORT_DIRECTION } from '@/constants/sort';
import { toggleSortDirection } from '@/utils/sort';
import {
  ChevronUpDownIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/20/solid';
import { useTranslation } from 'react-i18next';
import { OrganizationTableRow } from './OrganizationTableRow';
import { type OrganizationSort } from '../types';

const organizationTableFragment = graphql(
  `
    fragment OrganizationTableFragment on OrganizationPageCollection {
      items {
        ...OrganizationTableRowFragment
      }
      pageInfo {
        currentPage
        hasNextPage
        hasPreviousPage
        lastPage
        perPage
        totalCount
      }
    }
  `,
  [OrganizationTableRow.fragments.organization]
);

const emptyOrganizationItems: ReadonlyArray<
  FragmentOf<typeof OrganizationTableRow.fragments.organization>
> = [];

type OrganizationTableProps = {
  data?: FragmentOf<typeof organizationTableFragment>;
  isRefetching: boolean;
  isInitialLoading: boolean;
  error?: ApolloError;
  isUnauthenticated: boolean;
  onPageChange: (pageStart: number) => void;
  sort: OrganizationSort;
  onSortChange: (sort: OrganizationSort) => void;
};

export const OrganizationTable = ({
  data,
  isRefetching,
  isInitialLoading,
  error,
  isUnauthenticated,
  onPageChange,
  sort,
  onSortChange,
}: OrganizationTableProps) => {
  const { t } = useTranslation();

  const collection = data && readFragment(organizationTableFragment, data);
  const items = collection?.items ?? emptyOrganizationItems;
  const paginationInfo = collection?.pageInfo;

  const isLoading = isInitialLoading || isRefetching;

  const nextSort = (field: OrganizationSort['field']) => {
    const isNotCurrentSort = sort.field !== field;
    if (isNotCurrentSort) {
      return { field, direction: SORT_DIRECTION.ASC };
    }

    const nextDirection = toggleSortDirection(sort.direction);
    return { field, direction: nextDirection };
  };

  const renderSortIcon = (field: OrganizationSort['field']) => {
    if (sort.field !== field) {
      return (
        <ChevronUpDownIcon className="size-3.5 text-gray-400" aria-hidden />
      );
    }

    const Icon =
      sort.direction === SORT_DIRECTION.ASC ? ChevronUpIcon : ChevronDownIcon;
    return <Icon className="size-3.5 text-gray-700" aria-hidden />;
  };

  return (
    <section className="relative overflow-hidden rounded-sm rounded-tr-xl border-2 border-gray-200 bg-white">
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
          <LoaderScreen contained />
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500"
              >
                <button
                  type="button"
                  className="group inline-flex items-center gap-1 text-gray-500 hover:text-gray-800"
                  onClick={() => onSortChange(nextSort('NAME'))}
                  aria-label={t('sort.label.organizationName')}
                >
                  {t('組織名')}
                  {renderSortIcon('NAME')}
                </button>
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500"
              >
                {t('filter.label.publicationStatus')}
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500"
              >
                {t('filter.label.organizationType')}
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500"
              >
                <button
                  type="button"
                  className="group inline-flex items-center gap-1 text-gray-500 hover:text-gray-800"
                  onClick={() => onSortChange(nextSort('COUNTRY'))}
                  aria-label={t('sort.label.countryRegionalOrganization')}
                >
                  {t('filter.label.countryRegionalOrganization')}
                  {renderSortIcon('COUNTRY')}
                </button>
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500"
              >
                {t('filter.label.regionEconomicUnion')}
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500"
              >
                {t('最新記事日')}
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500"
              >
                {t('最古記事日')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {error && !isUnauthenticated ? (
              <tr>
                <td colSpan={7} className="p-4 py-10">
                  <div className="flex justify-center">
                    <Callout variant="danger" inline>
                      {t(
                        '組織の取得に失敗しました。時間をおいてから再度お試しください。'
                      )}
                    </Callout>
                  </div>
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="p-4 py-10 text-center text-sm text-gray-500"
                >
                  {t('該当する組織がありません。')}
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const organization = readFragment(
                  OrganizationTableRow.fragments.organization,
                  item
                );
                return (
                  <OrganizationTableRow key={organization.id} data={item} />
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {paginationInfo && items.length > 0 && (
        <div className="grid place-content-center border-t border-gray-100 bg-gray-50 px-4 py-3">
          <Pagination
            pageInfo={paginationInfo}
            onClick={onPageChange}
            pageRange={7}
            size="sm"
          />
        </div>
      )}
    </section>
  );
};

OrganizationTable.fragments = {
  collection: organizationTableFragment,
};
